import { Shield, Star, Crown, Gem } from 'lucide-react';

export const EASE = [0.16, 1, 0.3, 1];
export const VP   = { once: true, amount: 0.2 };

export const FELLOWSHIP_LEVELS = [
  {
    key:         'associate',
    label:       'Associate',
    code:        'A-XXXXX',
    benefit:     'Silver subscription — 30-day trial',
    detail:      'Earn 500 YOMs and refer 10 paid users within 30 days to become a Fellow. Miss the deadline and membership is deactivated.',
    tag:         'Entry',
    color:       '#1E3A8A',
    iconBg:      'rgba(30,58,138,0.10)',
    borderColor: 'rgba(30,58,138,0.20)',
    glassBg:     'rgba(30,58,138,0.04)',
    Icon:        Shield,
    brightColor: '#60a5fa',
    criteria: [
      { value: '500',  label: 'YOMs earned in your first 30 days' },
      { value: '10',   label: 'paid referrals (Silver / Gold / Platinum) in 30 days' },
      { value: '30',   label: 'day window — clock starts the moment you join' },
    ],
    warning:     'Miss the deadline and your membership is deactivated. Re-invitation required.',
    warningType: 'red',
    redeem:      0,
    maxTeam:     0,
  },
  {
    key:         'fellow',
    label:       'Fellow',
    code:        'F-XXXXX',
    benefit:     'Silver subscription — lifetime*',
    detail:      'Mentor up to 10 Associates. Maintain 1,000 YOMs + 15 paid referrals every month to keep Silver active. When 8 of your Associates hit their target, you rise to Senior Fellow.',
    tag:         'Silver',
    color:       '#0d3060',
    iconBg:      'rgba(13,48,96,0.10)',
    borderColor: 'rgba(13,48,96,0.20)',
    glassBg:     'rgba(13,48,96,0.04)',
    Icon:        Star,
    brightColor: '#93c5fd',
    criteria: [
      { value: '1,000',  label: 'YOMs required every 30 days to keep Silver active' },
      { value: '15',     label: 'paid referrals required every 30 days' },
      { value: '8 / 10', label: 'of your Associates must hit target → you rise to Senior Fellow' },
    ],
    warning:     'Miss a month — Silver reverts to Free. Fellowship stays active and restores next month you hit target.',
    warningType: 'amber',
    redeem:      250,
    maxTeam:     10,
    bonusYom:    100,
  },
  {
    key:         'senior_fellow',
    label:       'Senior Fellow',
    code:        'SF-XXXXX',
    benefit:     'Gold subscription — lifetime',
    detail:      'When 8 of your 10 direct Associates hit their own target (500 YOMs + 10 paid in 30 days), you are promoted automatically to Gold.',
    tag:         'Gold',
    color:       '#d97706',
    iconBg:      'rgba(217,119,6,0.10)',
    borderColor: 'rgba(217,119,6,0.22)',
    glassBg:     'rgba(217,119,6,0.04)',
    Icon:        Crown,
    brightColor: '#fbbf24',
    criteria: [
      { value: '8 / 20', label: 'of your direct Associates must rise to Senior Fellow level' },
      { value: '+100',   label: 'YOM bonus each time one of your Associates hits their target' },
      { value: 'up to 500', label: 'YOMs redeemable at Senior Fellow level' },
    ],
    redeem:   500,
    maxTeam:  20,
    bonusYom: 100,
  },
  {
    key:         'distinguished_fellow',
    label:       'Distinguished Fellow',
    code:        'DF-XXXXX',
    benefit:     'Platinum subscription — lifetime',
    detail:      'When 8 of your direct Associates rise to Senior Fellow level, you earn Platinum for life. The highest honour in the TeachGenie Fellowship.',
    tag:         'Platinum',
    color:       '#b45309',
    iconBg:      'rgba(180,83,9,0.10)',
    borderColor: 'rgba(245,158,11,0.25)',
    glassBg:     'rgba(245,158,11,0.04)',
    Icon:        Gem,
    brightColor: '#f59e0b',
    criteria: [
      { value: '8 / 20',    label: 'of your direct Associates must each become Senior Fellows' },
      { value: 'up to 1,000', label: 'YOMs redeemable — the highest limit in the Fellowship' },
      { value: '30',         label: 'educators in your direct mentorship network' },
    ],
    redeem:   1000,
    maxTeam:  30,
    bonusYom: 100,
  },
];

export const YOM_RATES = [
  { tier: 'Free',     yoms: 10,  color: '#6b7280', detail: 'For every free user you refer'      },
  { tier: 'Silver',   yoms: 30,  color: '#94a3b8', detail: 'For every Silver subscriber you refer' },
  { tier: 'Gold',     yoms: 60,  color: '#d97706', detail: 'For every Gold subscriber you refer'   },
  { tier: 'Platinum', yoms: 100, color: '#C8880A', detail: 'For every Platinum subscriber you refer' },
];

export const PLACEHOLDER_FELLOWS = [
  { id: 'p1', name: 'Dr. Priya Sharma',     level: 'senior_fellow',        title: 'Associate Professor',    image_url: 'https://randomuser.me/api/portraits/women/65.jpg', institution: 'Lovely Professional University' },
  { id: 'p2', name: 'Prof. Arjun Mehta',    level: 'fellow',               title: 'HOD — Computer Science', image_url: 'https://randomuser.me/api/portraits/men/32.jpg',   institution: 'Chandigarh University' },
  { id: 'p3', name: 'Ms. Sneha Krishnan',   level: 'associate',            title: 'Lecturer, Physics',      image_url: 'https://randomuser.me/api/portraits/women/44.jpg', institution: 'KL University' },
  { id: 'p4', name: 'Dr. Rajan Pillai',     level: 'distinguished_fellow', title: 'Dean of Academics',      image_url: 'https://randomuser.me/api/portraits/men/54.jpg',   institution: 'Amrita University' },
  { id: 'p5', name: 'Prof. Meena Iyer',     level: 'senior_fellow',        title: 'Professor of English',   image_url: 'https://randomuser.me/api/portraits/women/26.jpg', institution: 'Manipal University' },
  { id: 'p6', name: 'Dr. Karan Bhatia',     level: 'fellow',               title: 'Asst. Professor',        image_url: 'https://randomuser.me/api/portraits/men/43.jpg',   institution: 'SRM University' },
  { id: 'p7', name: 'Ms. Aarti Desai',      level: 'associate',            title: 'Teaching Fellow',        image_url: 'https://randomuser.me/api/portraits/women/58.jpg', institution: 'VIT University' },
  { id: 'p8', name: 'Prof. Suresh Reddy',   level: 'distinguished_fellow', title: 'Vice Principal',         image_url: 'https://randomuser.me/api/portraits/men/62.jpg',   institution: 'BITS Pilani' },
  { id: 'p9', name: 'Dr. Lakshmi Rajan',    level: 'senior_fellow',        title: 'Research Associate',     image_url: 'https://randomuser.me/api/portraits/women/72.jpg', institution: 'IIT Madras' },
];

export const LEVEL_BADGE_STYLES = {
  associate:            { background: 'rgba(140,146,158,0.12)', color: '#8C929E' },
  fellow:               { background: 'rgba(168,180,192,0.12)', color: '#A8B4C0' },
  senior_fellow:        { background: 'rgba(200,136,10,0.12)',  color: '#C8880A' },
  distinguished_fellow: { background: 'rgba(212,150,42,0.12)',  color: '#D4962A' },
};

export const LEVEL_LABELS = {
  associate:            'Associate',
  fellow:               'Fellow',
  senior_fellow:        'Senior Fellow',
  distinguished_fellow: 'Distinguished Fellow',
};

export const BENEFITS_TABLE = [
  {
    feature: 'Subscription Benefit',
    associate: 'Silver (30 days)',
    fellow: 'Silver (lifetime)',
    senior_fellow: 'Gold (lifetime)',
    distinguished_fellow: 'Platinum (lifetime)',
  },
  {
    feature: 'YOM Earnings',
    associate: '10–100 per referral',
    fellow: '10–100 per referral',
    senior_fellow: '10–100 per referral',
    distinguished_fellow: '10–100 per referral',
  },
  {
    feature: 'Max Team Size',
    associate: 'No team',
    fellow: '10 Associates',
    senior_fellow: '20 Associates',
    distinguished_fellow: '30 Associates',
  },
  {
    feature: 'Redeemable YOMs',
    associate: 'None',
    fellow: 'Up to 250',
    senior_fellow: 'Up to 500',
    distinguished_fellow: 'Up to 1,000',
  },
  {
    feature: 'Bonus on Team Win',
    associate: '—',
    fellow: '+100 YOMs',
    senior_fellow: '+100 YOMs',
    distinguished_fellow: '+100 YOMs',
  },
];
